import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { 
  Search, 
  Edit, 
  Trash2, 
  UserPlus,
  Eye,
  Filter,
  Mail,
  MessageCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Hash,
  Copy,
  CheckCheck,
  Play,
  Loader2
} from 'lucide-react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useDataCache } from '../../hooks/useDataCache';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';

interface EnrollmentData {
  id?: number;
  programId: number;
  groupId: number | null;
  isPrimary: boolean;
  enrollmentDate?: string;
  status: string;
  programName?: string;
  programCode?: string;
  groupName?: string;
  groupCode?: string;
  periodStatus?: string;
}

interface Alumno {
  id: number;
  numeroIdentificacionUnico: string;
  numeroReferenciaExterna: string | null;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string | null;
  email: string | null;
  telefono: string | null;
  estatus: 'aspirante' | 'aceptado' | 'activo' | 'egresado' | 'baja_temporal' | 'baja_definitiva' | 'baja_administrativa' | 'en_espera' | 'prospecto' | 'deudor' | 'programa_concluido' | 'liberado' | 'graduado' | 'titulado' | 'non_grata';
  programId?: number | null;
  groupId?: number | null;
  enrollments?: EnrollmentData[];
  fechaCreacion: string;
  fechaActualizacion: string;
}

interface AcademicProgram {
  id: number;
  code: string;
  name: string;
  level: string;
  isActive: boolean;
}

interface AcademicPeriod {
  id: number;
  code: string;
  name: string;
  status: string;
  programId: number | null;
}

interface AttendanceSummary {
  courseId: number;
  courseName: string;
  courseCode: string;
  totalClasses: number;
  present: number;
  absent: number;
  justified: number;
  late: number;
}

interface CourseInfo {
  courseId: number;
  courseName: string;
  courseCode: string;
  hoursTheory: number | null;
  hoursPractice: number | null;
  scheduleId: number;
  day: string;
  startTime: string;
  endTime: string;
  professorName: string | null;
}

interface AcademicRecord {
  student: {
    id: number;
    nombre: string;
    apellidoPaterno: string;
  };
  program: {
    id: number;
    name: string;
    code: string;
  } | null;
  period: {
    id: number;
    name: string;
    code: string;
    startDate: string;
    endDate: string;
  } | null;
  courses: CourseInfo[];
  attendanceSummary: AttendanceSummary[];
}

interface EnrollmentFormEntry {
  programId: number | null;
  groupId: number | null;
  isPrimary: boolean;
  status: string;
}

interface TuitionPlanPreview {
  id: number;
  name: string;
  installmentCount: number;
  totalAmount: string;
  firstDueDate: string;
  intervalDays: number;
  discountPct: string;
  surchargePct: string;
  graceDays: number;
}

interface InscriptionFeePreview {
  productId: number;
  productName: string;
  price: string;
  taxRate: string;
}

interface EnrollmentPaymentPreview {
  groupId: number;
  groupName: string;
  inscriptionFee: InscriptionFeePreview | null;
  activeTuitionPlans: TuitionPlanPreview[];
}

interface AlumnoFormData {
  numeroReferenciaExterna: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  email: string;
  telefono: string;
  fechaNacimiento: string;
  lugarNacimiento: string;
  nacionalidad: string;
  escolaridad: string;
  ocupacion: string;
  domicilio: string;
  contactoEmergencia: string;
  telefonoEmergencia: string;
  estatus: 'aspirante' | 'aceptado' | 'activo' | 'egresado' | 'baja_temporal' | 'baja_definitiva' | 'baja_administrativa' | 'en_espera' | 'prospecto' | 'deudor' | 'programa_concluido' | 'liberado' | 'graduado' | 'titulado' | 'non_grata';
  programId: number | null;
  groupId: number | null;
  enrollments: EnrollmentFormEntry[];
  duracion: string;
  diaClases: string;
  horario: string;
  fechaInicio: string;
  comoConocio: string;
  tieneAlergias: boolean;
  padecimientoMedico: string;
  tieneTratamiento: boolean;
  tratamientoMedico: string;
  metadata: string | null;
}

interface CodigoPago {
  codigoPago: string;
  sku: string;
  conceptoNombre: string;
  referencia: string;
  fuente: 'factura' | 'grupo';
}

interface CodigosPagoData {
  alumno: { id: number; nui: string; nuiDigits: string; nombre: string; };
  codigos: CodigoPago[];
}

interface StatusHistoryEntry {
  id: number;
  oldStatus: string | null;
  newStatus: string;
  reason: string | null;
  autoTriggered: boolean;
  createdAt: string;
  changedByName: string | null;
}

interface StatusEvalEntry {
  id: number;
  trigger: string;
  ruleId: string;
  evaluatedAt: string;
  currentStatus: string | null;
  proposedStatus: string | null;
  applied: boolean;
  skipReason: string | null;
  conditionSnapshot: Record<string, unknown> | null;
}

export default function Alumnos() {
  const { getAlumnosData, createAlumno, invalidateCache } = useDataCache();
  const { user } = useAuth();
  const { toast } = useToast();
  const canManageStatus = user?.role === 'Admin' || (user?.permissions?.includes('alumnos:manage-status') ?? false);
  const [alumnos, setAlumnos] = useState<Alumno[]>([]);
  const [academicPrograms, setAcademicPrograms] = useState<AcademicProgram[]>([]);
  const [academicPeriods, setAcademicPeriods] = useState<AcademicPeriod[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [editingAlumno, setEditingAlumno] = useState<Alumno | null>(null);
  const [detailAlumno, setDetailAlumno] = useState<Alumno | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [groupFilter, setGroupFilter] = useState<string>('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const wasSearchFocused = useRef(false);

  const [columnFilters, setColumnFilters] = useState({
    nui: '', apellidoPaterno: '', apellidoMaterno: '',
    nombre: '', email: '', telefono: '', estatus: '', programa: '', grupo: '',
  });
  const setColFilter = (key: keyof typeof columnFilters, val: string) =>
    setColumnFilters(prev => ({ ...prev, [key]: val }));
  const hasColFilters = Object.values(columnFilters).some(v => v !== '');
  const clearColFilters = () => {
    setColumnFilters({ nui: '', apellidoPaterno: '', apellidoMaterno: '', nombre: '', email: '', telefono: '', estatus: '', programa: '', grupo: '' });
    setCurrentPage(1);
  };

  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Estados para envío de correo de bienvenida
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedAlumnoForEmail, setSelectedAlumnoForEmail] = useState<Alumno | null>(null);
  const [tempEmail, setTempEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Estados para historial académico
  const [academicRecord, setAcademicRecord] = useState<AcademicRecord | null>(null);
  const [loadingAcademicRecord, setLoadingAcademicRecord] = useState(false);
  const [detailTab, setDetailTab] = useState<'info' | 'academic' | 'status'>('info');

  // Estados para historial de estatus
  const [statusHistory, setStatusHistory] = useState<StatusHistoryEntry[]>([]);
  const [statusEvaluations, setStatusEvaluations] = useState<StatusEvalEntry[]>([]);
  const [loadingStatus, setLoadingStatus] = useState(false);

  // Batch status evaluator
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchDone, setBatchDone] = useState<{ applied: number; total: number } | null>(null);

  // Estados para códigos de pago
  const [showCodigosPago, setShowCodigosPago] = useState(false);
  const [codigosPagoData, setCodigosPagoData] = useState<CodigosPagoData | null>(null);
  const [loadingCodigos, setLoadingCodigos] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Estados para advertencia de posible duplicado al crear alumno
  type DuplicateCandidate = {
    id: number; nombre: string; apellidoPaterno: string; apellidoMaterno: string;
    email: string; telefono: string; estatus: string; numeroIdentificacionUnico: string;
  };
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateCandidates, setDuplicateCandidates] = useState<DuplicateCandidate[]>([]);
  const [pendingCreatePayload, setPendingCreatePayload] = useState<any | null>(null);

  // Estados para validación de pago en nueva inscripción
  const [originalGroupIds, setOriginalGroupIds] = useState<Set<number>>(new Set());
  const [showPaymentConfirmDialog, setShowPaymentConfirmDialog] = useState(false);
  const [paymentPreviews, setPaymentPreviews] = useState<EnrollmentPaymentPreview[]>([]);
  const [paymentPreviewLoading, setPaymentPreviewLoading] = useState(false);
  const [pendingSavePayload, setPendingSavePayload] = useState<any | null>(null);

  // Estados para ordenamiento de columnas
  type SortKey = 'nui' | 'apellidoPaterno' | 'apellidoMaterno' | 'nombre' | 'email' | 'telefono' | 'estatus' | 'programa' | 'grupo';
  type SortDir = 'asc' | 'desc';
  const [sortKey, setSortKey] = useState<SortKey>('apellidoPaterno');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortKey !== column) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
  };

  const filteredAlumnos = useMemo(() => {
    const cf = columnFilters;
    return alumnos.filter(a => {
      if (cf.nui && !a.numeroIdentificacionUnico.toLowerCase().includes(cf.nui.toLowerCase())) return false;
      if (cf.apellidoPaterno && !a.apellidoPaterno.toLowerCase().includes(cf.apellidoPaterno.toLowerCase())) return false;
      if (cf.apellidoMaterno && !(a.apellidoMaterno || '').toLowerCase().includes(cf.apellidoMaterno.toLowerCase())) return false;
      if (cf.nombre && !a.nombre.toLowerCase().includes(cf.nombre.toLowerCase())) return false;
      if (cf.email && !(a.email || '').toLowerCase().includes(cf.email.toLowerCase())) return false;
      if (cf.telefono && !(a.telefono || '').includes(cf.telefono)) return false;
      if (cf.estatus && a.estatus !== cf.estatus) return false;
      if (cf.programa) {
        const progName = academicPrograms.find(p => p.id === a.programId)?.name || '';
        if (!progName.toLowerCase().includes(cf.programa.toLowerCase())) return false;
      }
      if (cf.grupo) {
        const grpName = academicPeriods.find(p => p.id === a.groupId)?.name || '';
        if (!grpName.toLowerCase().includes(cf.grupo.toLowerCase())) return false;
      }
      return true;
    });
  }, [alumnos, columnFilters, academicPrograms, academicPeriods]);

  const emptyEnrollment: EnrollmentFormEntry = { programId: null, groupId: null, isPrimary: true, status: 'activo' };

  const [newAlumno, setNewAlumno] = useState<AlumnoFormData>({
    numeroReferenciaExterna: '',
    nombre: '',
    apellidoPaterno: '',
    apellidoMaterno: '',
    email: '',
    telefono: '',
    fechaNacimiento: '',
    lugarNacimiento: '',
    nacionalidad: 'México',
    escolaridad: '',
    ocupacion: '',
    domicilio: '',
    contactoEmergencia: '',
    telefonoEmergencia: '',
    estatus: 'aspirante',
    programId: null,
    groupId: null,
    enrollments: [{ ...emptyEnrollment }],
    duracion: '',
    diaClases: '',
    horario: '',
    fechaInicio: '',
    comoConocio: '',
    tieneAlergias: false,
    padecimientoMedico: '',
    tieneTratamiento: false,
    tratamientoMedico: '',
    metadata: ''
  });

  const [editAlumno, setEditAlumno] = useState<AlumnoFormData>({
    numeroReferenciaExterna: '',
    nombre: '',
    apellidoPaterno: '',
    apellidoMaterno: '',
    email: '',
    telefono: '',
    fechaNacimiento: '',
    lugarNacimiento: '',
    nacionalidad: 'México',
    escolaridad: '',
    ocupacion: '',
    domicilio: '',
    contactoEmergencia: '',
    telefonoEmergencia: '',
    estatus: 'aspirante',
    programId: null,
    groupId: null,
    enrollments: [{ ...emptyEnrollment }],
    duracion: '',
    diaClases: '',
    horario: '',
    fechaInicio: '',
    comoConocio: '',
    tieneAlergias: false,
    padecimientoMedico: '',
    tieneTratamiento: false,
    tratamientoMedico: '',
    metadata: ''
  });

  // Cargar programas académicos y períodos/grupos
  const loadAcademicData = async () => {
    try {
      const [programsRes, periodsRes] = await Promise.all([
        fetch('/api/academic-programs', { credentials: 'include' }),
        fetch('/api/academic-periods', { credentials: 'include' })
      ]);
      
      if (programsRes.ok) {
        const programs = await programsRes.json();
        setAcademicPrograms(programs.filter((p: AcademicProgram) => p.isActive));
      }
      
      if (periodsRes.ok) {
        const periods = await periodsRes.json();
        setAcademicPeriods(periods);
      }
    } catch (error) {
      console.error('Error loading academic data:', error);
    }
  };

  // Cargar historial de estatus y evaluaciones del engine
  const loadStatusData = async (alumnoId: number) => {
    try {
      setLoadingStatus(true);
      const [histRes, evalRes] = await Promise.all([
        fetch(`/api/alumnos/${alumnoId}/status-history`, { credentials: 'include' }),
        fetch(`/api/alumnos/${alumnoId}/status-evaluations`, { credentials: 'include' }),
      ]);
      setStatusHistory(histRes.ok ? await histRes.json() : []);
      setStatusEvaluations(evalRes.ok ? await evalRes.json() : []);
    } catch {
      setStatusHistory([]);
      setStatusEvaluations([]);
    } finally {
      setLoadingStatus(false);
    }
  };

  // Cargar historial académico del alumno
  const loadAcademicRecord = async (alumnoId: number) => {
    try {
      setLoadingAcademicRecord(true);
      const response = await fetch(`/api/alumnos/${alumnoId}/academic-record`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setAcademicRecord(data);
      } else {
        setAcademicRecord(null);
      }
    } catch (error) {
      console.error('Error loading academic record:', error);
      setAcademicRecord(null);
    } finally {
      setLoadingAcademicRecord(false);
    }
  };

  // Cargar lista de alumnos llamando directamente al backend con todos los filtros
  const loadAlumnos = async () => {
    try {
      setLoading(true);
      setError(null);

      const hasActiveColFilters = Object.values(columnFilters).some(v => v !== '');

      const params = new URLSearchParams();
      // Cuando hay filtros de columna activos, cargamos todos los registros
      // para que el filtrado client-side vea el dataset completo, no solo la página actual.
      if (hasActiveColFilters) {
        params.set('page', '1');
        params.set('limit', '1000');
      } else {
        params.set('page', String(currentPage));
        params.set('limit', '10');
      }
      if (searchTerm.trim()) params.set('search', searchTerm.trim());
      if (statusFilter) params.set('estatus', statusFilter);

      const response = await fetch(`/api/alumnos?${params.toString()}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Error al cargar alumnos');
      const data = await response.json();

      let filteredAlumnos: any[] = data.alumnos || [];

      // groupFilter se aplica client-side sobre la página actual
      if (groupFilter) {
        filteredAlumnos = filteredAlumnos.filter(
          (alumno: any) => (alumno.groupId || 0).toString() === groupFilter
        );
      }

      // Ordenamiento client-side sobre la página actual
      const getValue = (alumno: any, key: SortKey): string => {
        switch (key) {
          case 'nui': return (alumno.numeroIdentificacionUnico || '').toLowerCase();
          case 'apellidoPaterno': return (alumno.apellidoPaterno || '').toLowerCase();
          case 'apellidoMaterno': return (alumno.apellidoMaterno || '').toLowerCase();
          case 'nombre': return (alumno.nombre || '').toLowerCase();
          case 'email': return (alumno.email || '').toLowerCase();
          case 'telefono': return (alumno.telefono || '').toLowerCase();
          case 'estatus': return (alumno.estatus || '').toLowerCase();
          case 'programa': return (academicPrograms.find(p => p.id === alumno.programId)?.name || '').toLowerCase();
          case 'grupo': return (academicPeriods.find(p => p.id === alumno.groupId)?.name || '').toLowerCase();
          default: return '';
        }
      };
      const dir = sortDir === 'asc' ? 1 : -1;
      filteredAlumnos.sort((a: any, b: any) => {
        const valA = getValue(a, sortKey);
        const valB = getValue(b, sortKey);
        return dir * valA.localeCompare(valB);
      });

      setAlumnos(filteredAlumnos);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error al cargar alumnos');
      console.error('Error loading alumnos:', error);
    } finally {
      setLoading(false);
      if (wasSearchFocused.current) {
        requestAnimationFrame(() => {
          if (wasSearchFocused.current && searchInputRef.current) {
            searchInputRef.current.focus();
          }
        });
      }
    }
  };

  // Cargar programas académicos y períodos al montar el componente
  useEffect(() => {
    loadAcademicData();
  }, []);

  // Invalidate cache when filters change to ensure fresh data
  useEffect(() => {
    invalidateCache('alumnos');
  }, [statusFilter, groupFilter]);

  useEffect(() => {
    loadAlumnos();
  }, [currentPage, searchTerm, statusFilter, groupFilter, sortKey, sortDir]);

  // Búsqueda con debounce
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadAlumnos();
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, statusFilter, groupFilter]);

  // Filtros de columna con debounce — recarga TODOS los registros para que el
  // filtrado client-side vea el dataset completo (no solo la página actual).
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1); // el cambio de página dispara el useEffect principal
      } else {
        loadAlumnos();
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [columnFilters]);

  // Ejecutar creación real (después de que el usuario confirmó o no hay duplicados)
  // Muestra un toast visible con el resumen de cuotas/facturas de colegiatura generadas
  // automáticamente al crear o editar un alumno. Sin esto, la generación exitosa (o su
  // ausencia) pasaba desapercibida para quien hace la inscripción.
  const notifyPaymentsGenerated = (paymentsGenerated?: { installmentsCreated: number; invoicesCreated: number }[]) => {
    if (!paymentsGenerated || paymentsGenerated.length === 0) return;
    const totals = paymentsGenerated.reduce(
      (acc, p) => ({
        installments: acc.installments + (p.installmentsCreated || 0),
        invoices: acc.invoices + (p.invoicesCreated || 0),
      }),
      { installments: 0, invoices: 0 }
    );
    if (totals.installments === 0 && totals.invoices === 0) return;
    toast({
      title: 'Cuentas de cobro generadas',
      description: `Se generaron ${totals.installments} cuota(s) de colegiatura y ${totals.invoices} factura(s).`,
    });
  };

  const performCreateAlumno = async (payload: any) => {
    setCreateLoading(true);
    setError(null);
    setFieldErrors({});
    try {
      const result = await createAlumno(payload);
      notifyPaymentsGenerated(result?.paymentsGenerated);
      setShowDuplicateWarning(false);
      setPendingCreatePayload(null);

      setShowCreateForm(false);
      setNewAlumno({
        numeroReferenciaExterna: '',
        nombre: '',
        apellidoPaterno: '',
        apellidoMaterno: '',
        email: '',
        telefono: '',
        fechaNacimiento: '',
        lugarNacimiento: '',
        nacionalidad: 'México',
        escolaridad: '',
        ocupacion: '',
        domicilio: '',
        contactoEmergencia: '',
        telefonoEmergencia: '',
        estatus: 'aspirante',
        programId: null,
        groupId: null,
        enrollments: [{ ...emptyEnrollment }],
        duracion: '',
        diaClases: '',
        horario: '',
        fechaInicio: '',
        comoConocio: '',
        tieneAlergias: false,
        padecimientoMedico: '',
        tieneTratamiento: false,
        tratamientoMedico: '',
        metadata: null
      });
      loadAlumnos();
    } catch (error: any) {
      const fieldMap: Record<string, string> = error?.fieldMap || {};
      if (Object.keys(fieldMap).length > 0) {
        setFieldErrors(fieldMap);
        setError('Por favor corrige los errores marcados en el formulario');
      } else {
        setError(error instanceof Error ? error.message : 'Error al crear alumno');
      }
    } finally {
      setCreateLoading(false);
    }
  };

  // Verificar duplicados y luego crear alumno
  const handleCreateAlumno = async () => {
    setError(null);
    setFieldErrors({});

    const validEnrollments = newAlumno.enrollments
      .filter(e => e.programId)
      .map(e => ({ programId: e.programId!, groupId: e.groupId, isPrimary: e.isPrimary, status: e.status }));

    const payload = {
      ...newAlumno,
      enrollments: validEnrollments.length > 0 ? validEnrollments : undefined,
      programId: validEnrollments.length > 0 ? validEnrollments[0].programId : newAlumno.programId,
      groupId: validEnrollments.length > 0 ? validEnrollments[0].groupId : newAlumno.groupId,
    };

    // Verificar posibles duplicados antes de guardar
    try {
      const params = new URLSearchParams();
      if (newAlumno.nombre) params.set('nombre', newAlumno.nombre.trim());
      if (newAlumno.apellidoPaterno) params.set('apellidoPaterno', newAlumno.apellidoPaterno.trim());
      if (newAlumno.email) params.set('email', newAlumno.email.trim());

      const dupRes = await fetch(`/api/alumnos/check-duplicate?${params.toString()}`, { credentials: 'include' });
      if (dupRes.ok) {
        const { duplicates } = await dupRes.json();
        if (duplicates && duplicates.length > 0) {
          setDuplicateCandidates(duplicates);
          setPendingCreatePayload(payload);
          setShowDuplicateWarning(true);
          return;
        }
      }
    } catch {
      // Si falla la verificación, continuar con la creación de todas formas
    }

    await performCreateAlumno(payload);
  };

  // Ejecutar el save real del alumno con flag de pagos
  const performSaveAlumno = async (payload: any, generatePayments: boolean) => {
    if (!editingAlumno) return;
    setEditLoading(true);
    setError(null);
    setFieldErrors({});
    try {
      const response = await fetch(`/api/alumnos/${editingAlumno.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...payload, generatePayments }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        const err: any = new Error(errorData.error || 'Error al actualizar alumno');
        if (errorData.fields) err.fieldMap = errorData.fields;
        throw err;
      }
      const result = await response.json();
      invalidateCache('alumnos');
      setShowEditForm(false);
      setEditingAlumno(null);
      setShowPaymentConfirmDialog(false);
      setPendingSavePayload(null);
      loadAlumnos();
      notifyPaymentsGenerated(result.paymentsGenerated);
    } catch (error: any) {
      const fieldMap: Record<string, string> = error?.fieldMap || {};
      if (Object.keys(fieldMap).length > 0) {
        setFieldErrors(fieldMap);
        setError('Por favor corrige los errores marcados en el formulario');
      } else {
        setError(error instanceof Error ? error.message : 'Error al actualizar alumno');
      }
    } finally {
      setEditLoading(false);
    }
  };

  // Editar alumno — intercepta para mostrar preview de pagos si hay grupos nuevos
  const handleEditAlumno = async () => {
    if (!editingAlumno) return;
    setError(null);
    setFieldErrors({});

    // Build enrollments for the API
    const validEnrollments = editAlumno.enrollments
      .filter(e => e.programId)
      .map(e => ({
        programId: e.programId!,
        groupId: e.groupId,
        isPrimary: e.isPrimary,
        status: e.status,
      }));

    const payload = {
      ...editAlumno,
      enrollments: validEnrollments.length > 0 ? validEnrollments : undefined,
      programId: validEnrollments.length > 0 ? (validEnrollments.find(e => e.isPrimary) || validEnrollments[0]).programId : editAlumno.programId,
      groupId: validEnrollments.length > 0 ? (validEnrollments.find(e => e.isPrimary) || validEnrollments[0]).groupId : editAlumno.groupId,
    };

    // Detect newly added groups
    const newGroupIds = validEnrollments
      .map(e => e.groupId)
      .filter((gId): gId is number => !!gId && !originalGroupIds.has(gId));

    if (newGroupIds.length > 0) {
      // Fetch payment preview for each new group
      setPaymentPreviewLoading(true);
      try {
        const previews: EnrollmentPaymentPreview[] = [];
        for (const gId of newGroupIds) {
          const r = await fetch(`/api/alumnos/enrollment-preview?groupId=${gId}`, { credentials: 'include' });
          if (r.ok) {
            const data = await r.json();
            const groupName = academicPeriods.find(p => p.id === gId)?.name || `Grupo #${gId}`;
            if (data.inscriptionFee || (data.activeTuitionPlans && data.activeTuitionPlans.length > 0)) {
              previews.push({ groupId: gId, groupName, inscriptionFee: data.inscriptionFee, activeTuitionPlans: data.activeTuitionPlans });
            }
          }
        }
        if (previews.length > 0) {
          setPaymentPreviews(previews);
          setPendingSavePayload(payload);
          setShowPaymentConfirmDialog(true);
          return;
        }
      } finally {
        setPaymentPreviewLoading(false);
      }
    }

    // No new financial obligations — save directly without payment generation
    await performSaveAlumno(payload, false);
  };

  // Eliminar alumno
  const handleOpenCodigosPago = async (alumno: Alumno) => {
    setLoadingCodigos(true);
    setCodigosPagoData(null);
    setShowCodigosPago(true);
    try {
      const res = await fetch(`/api/alumnos/${alumno.id}/codigos-pago`);
      if (!res.ok) throw new Error('Error al obtener códigos');
      const data: CodigosPagoData = await res.json();
      setCodigosPagoData(data);
    } catch {
      setCodigosPagoData(null);
    } finally {
      setLoadingCodigos(false);
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    });
  };

  const handleDeleteAlumno = async (id: number) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este alumno?')) {
      return;
    }

    try {
      setError(null);
      const response = await fetch(`/api/alumnos/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al eliminar alumno');
      }

      // Invalidate cache to ensure fresh data is loaded
      invalidateCache('alumnos');
      loadAlumnos();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error al eliminar alumno');
    }
  };

  // Manejar envío de correo de bienvenida
  const handleSendWelcomeEmail = (alumno: Alumno) => {
    if (!alumno.email) {
      // No tiene email, mostrar modal para solicitarlo
      setSelectedAlumnoForEmail(alumno);
      setTempEmail('');
      setShowEmailModal(true);
    } else {
      // Tiene email, enviar directamente
      handleSendEmail(alumno, alumno.email);
    }
  };

  // Enviar correo de bienvenida
  const handleSendEmail = async (alumno: Alumno, emailAddress: string) => {
    try {
      setSendingEmail(true);
      setError(null);

      const response = await fetch(`/api/alumnos/${alumno.id}/send-welcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: emailAddress })
      });

      const data = await response.json();

      if (response.ok) {
        alert('Correo de bienvenida enviado exitosamente a ' + emailAddress);
        setShowEmailModal(false);
        setSelectedAlumnoForEmail(null);
        setTempEmail('');
      } else {
        throw new Error(data.error || 'Error al enviar correo');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error al enviar correo');
      alert('Error al enviar correo: ' + (error instanceof Error ? error.message : 'Error desconocido'));
      console.error('Error sending welcome email:', error);
    } finally {
      setSendingEmail(false);
    }
  };

  // Abrir formulario de edición
  const openEditForm = async (alumno: Alumno) => {
    try {
      setEditLoading(true);
      setError(null);
      
      // Obtener datos completos del alumno
      const response = await fetch(`/api/alumnos/${alumno.id}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Error al cargar datos del alumno');
      }
      
      const fullData = await response.json();
      
      setEditingAlumno(alumno);

      // Build enrollments from response data
      const loadedEnrollments: EnrollmentFormEntry[] = fullData.enrollments && fullData.enrollments.length > 0
        ? fullData.enrollments.map((e: any) => ({
            programId: e.programId,
            groupId: e.groupId,
            isPrimary: e.isPrimary,
            status: e.status || 'activo',
          }))
        : fullData.programId
          ? [{ programId: fullData.programId, groupId: fullData.groupId || null, isPrimary: true, status: 'activo' }]
          : [{ ...emptyEnrollment }];

      // Capture original group IDs for diff detection later
      const ogIds = new Set<number>(
        loadedEnrollments.map(e => e.groupId).filter((id): id is number => !!id)
      );
      setOriginalGroupIds(ogIds);

      setEditAlumno({
        numeroReferenciaExterna: fullData.numeroReferenciaExterna || '',
        nombre: fullData.nombre || '',
        apellidoPaterno: fullData.apellidoPaterno || '',
        apellidoMaterno: fullData.apellidoMaterno || '',
        email: fullData.email || '',
        telefono: fullData.telefono || '',
        fechaNacimiento: fullData.fechaNacimiento || '',
        lugarNacimiento: fullData.lugarNacimiento || '',
        nacionalidad: fullData.nacionalidad || 'México',
        escolaridad: fullData.escolaridad || '',
        ocupacion: fullData.ocupacion || '',
        domicilio: fullData.domicilio || '',
        contactoEmergencia: fullData.contactoEmergencia || '',
        telefonoEmergencia: fullData.telefonoEmergencia || '',
        estatus: fullData.estatus || 'aspirante',
        programId: fullData.programId || null,
        groupId: fullData.groupId || null,
        enrollments: loadedEnrollments,
        duracion: fullData.duracion || '',
        diaClases: fullData.diaClases || '',
        horario: fullData.horario || '',
        fechaInicio: fullData.fechaInicio || '',
        comoConocio: (typeof fullData.comoConocio === 'string' ? fullData.comoConocio : '') || '',
        tieneAlergias: fullData.tieneAlergias || false,
        padecimientoMedico: (typeof fullData.padecimientoMedico === 'string' ? fullData.padecimientoMedico : '') || '',
        tieneTratamiento: fullData.tieneTratamiento || false,
        tratamientoMedico: (typeof fullData.tratamientoMedico === 'string' ? fullData.tratamientoMedico : '') || '',
        metadata: fullData.metadata || ''
      });
      setError(null);
      setFieldErrors({});
      setShowEditForm(true);
    } catch (error) {
      console.error('Error loading alumno data:', error);
      setError(error instanceof Error ? error.message : 'Error al cargar datos del alumno');
    } finally {
      setEditLoading(false);
    }
  };

  // Ver detalles del alumno
  const viewDetails = async (alumno: Alumno) => {
    setDetailTab('info');
    setAcademicRecord(null);
    setStatusHistory([]);
    setStatusEvaluations([]);
    setShowDetails(true);
    
    // Fetch full details including enrollments
    try {
      const response = await fetch(`/api/alumnos/${alumno.id}`, { credentials: 'include' });
      if (response.ok) {
        const fullData = await response.json();
        setDetailAlumno(fullData);
      } else {
        setDetailAlumno(alumno);
      }
    } catch {
      setDetailAlumno(alumno);
    }
    loadAcademicRecord(alumno.id);
    loadStatusData(alumno.id);
  };

  // Obtener color para el estatus
  const getStatusColor = (estatus: string) => {
    switch (estatus) {
      // Flujo de ingreso
      case 'prospecto': return 'text-slate-600 bg-slate-100';
      case 'aspirante': return 'text-yellow-700 bg-yellow-100';
      case 'aceptado': return 'text-blue-700 bg-blue-100';
      case 'activo': return 'text-green-700 bg-green-100';
      // Situación financiera
      case 'deudor': return 'text-orange-700 bg-orange-100';
      // Flujo de egreso
      case 'programa_concluido': return 'text-indigo-700 bg-indigo-100';
      case 'liberado': return 'text-violet-700 bg-violet-100';
      case 'graduado': return 'text-purple-700 bg-purple-100';
      case 'egresado': return 'text-purple-800 bg-purple-100';
      case 'titulado': return 'text-fuchsia-700 bg-fuchsia-100';
      // Bajas
      case 'baja_temporal': return 'text-orange-600 bg-orange-50';
      case 'baja_definitiva': return 'text-red-700 bg-red-100';
      case 'baja_administrativa': return 'text-red-800 bg-red-100';
      case 'en_espera': return 'text-sky-700 bg-sky-100';
      case 'non_grata': return 'text-white bg-gray-900';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (estatus: string) => {
    switch (estatus) {
      case 'prospecto': return 'Prospecto';
      case 'aspirante': return 'Aspirante';
      case 'aceptado': return 'Aceptado';
      case 'activo': return 'Activo';
      case 'deudor': return 'Deudor';
      case 'programa_concluido': return 'Programa Concluido';
      case 'liberado': return 'Liberado';
      case 'graduado': return 'Graduado';
      case 'egresado': return 'Egresado';
      case 'titulado': return 'Titulado';
      case 'baja_temporal': return 'Baja Temporal';
      case 'baja_definitiva': return 'Baja Definitiva';
      case 'baja_administrativa': return 'Baja Administrativa';
      case 'en_espera': return 'En Espera';
      case 'non_grata': return 'NON GRATA';
      default: return estatus;
    }
  };

  if (loading && alumnos.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center">
          <p>Cargando alumnos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold">Alumnos</h1>
          <p className="text-gray-600">Gestión de estudiantes y aspirantes</p>
        </div>
        <div className="flex items-center gap-2">
          {canManageStatus && (
            <Button
              variant="outline"
              onClick={async () => {
                setBatchRunning(true);
                setBatchDone(null);
                try {
                  const res = await fetch('/api/alumnos/batch-evaluate-statuses', {
                    method: 'POST', credentials: 'include',
                  });
                  if (res.ok) {
                    const data = await res.json();
                    setBatchDone({ applied: data.applied, total: data.total });
                  }
                } finally {
                  setBatchRunning(false);
                }
              }}
              disabled={batchRunning}
              className="flex items-center gap-2"
            >
              {batchRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {batchRunning ? 'Evaluando...' : 'Evaluar Estatus'}
            </Button>
          )}
          {batchDone && !batchRunning && (
            <span className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded-full">
              {batchDone.applied} cambios / {batchDone.total} evaluados
            </span>
          )}
          <Button onClick={() => { setError(null); setFieldErrors({}); setShowCreateForm(true); }} className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            Nuevo Alumno
          </Button>
        </div>
      </div>

      {/* Error Display (solo visible cuando no hay modal abierto) */}
      {error && !showCreateForm && !showEditForm && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-800">⚠ {error}</p>
              {Object.keys(fieldErrors).length > 0 && (
                <ul className="mt-1 list-disc list-inside space-y-0.5">
                  {Object.entries(fieldErrors).map(([field, msg]) => (
                    <li key={field} className="text-xs text-red-700">{msg}</li>
                  ))}
                </ul>
              )}
            </div>
            <button
              onClick={() => { setError(null); setFieldErrors({}); }}
              className="shrink-0 text-red-400 hover:text-red-600 text-lg leading-none"
              aria-label="Cerrar"
            >✕</button>
          </div>
        </div>
      )}

      {/* Filtros y búsqueda */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  ref={searchInputRef}
                  placeholder="Buscar por nombre, email, folio..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => { wasSearchFocused.current = true; }}
                  onBlur={() => { wasSearchFocused.current = false; }}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-full sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">Todos los estatus</option>
                <optgroup label="── Flujo de ingreso">
                  <option value="aspirante">Aspirante</option>
                  <option value="aceptado">Aceptado</option>
                  <option value="activo">Activo</option>
                </optgroup>
                <optgroup label="── Situación financiera">
                  <option value="deudor">Deudor</option>
                </optgroup>
                <optgroup label="── Flujo de egreso">
                  <option value="programa_concluido">Programa Concluido</option>
                  <option value="liberado">Liberado</option>
                  <option value="graduado">Graduado</option>
                  <option value="egresado">Egresado</option>
                  <option value="titulado">Titulado</option>
                </optgroup>
                <optgroup label="── Bajas">
                  <option value="baja_temporal">Baja Temporal</option>
                  <option value="baja_definitiva">Baja Definitiva</option>
                  <option value="baja_administrativa">Baja Administrativa</option>
                  <option value="en_espera">En Espera</option>
                  <option value="non_grata">NON GRATA</option>
                </optgroup>
              </select>
            </div>
            <div className="w-full sm:w-64">
              <SearchableSelect
                options={academicPeriods.map((period) => ({
                  value: period.id.toString(),
                  label: `${period.code} - ${period.name}`,
                }))}
                value={groupFilter || null}
                onChange={(val) => setGroupFilter(val ? val.toString() : '')}
                placeholder="Todos los grupos"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de alumnos */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Lista de Alumnos</CardTitle>
              <CardDescription>
                {hasColFilters
                  ? `${filteredAlumnos.length} de ${alumnos.length} alumnos coinciden con los filtros`
                  : `${alumnos.length} alumnos — Página ${currentPage} de ${totalPages}`}
              </CardDescription>
            </div>
            {hasColFilters && (
              <button
                onClick={clearColFilters}
                className="flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 border border-red-200 hover:border-red-300 rounded-md px-2.5 py-1.5 bg-red-50 hover:bg-red-100 transition-colors"
              >
                <span>✕</span> Limpiar filtros de columna
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[1100px]">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4">Acciones</th>
                  {([
                    { key: 'nui' as SortKey, label: 'NUI', title: 'Número de Identificación Único' },
                    { key: 'apellidoPaterno' as SortKey, label: 'Apellido Paterno' },
                    { key: 'apellidoMaterno' as SortKey, label: 'Apellido Materno' },
                    { key: 'nombre' as SortKey, label: 'Nombre(s)' },
                    { key: 'email' as SortKey, label: 'Email' },
                    { key: 'telefono' as SortKey, label: 'Teléfono' },
                    { key: 'estatus' as SortKey, label: 'Estatus' },
                    { key: 'programa' as SortKey, label: 'Programa' },
                    { key: 'grupo' as SortKey, label: 'Grupo' },
                  ]).map(col => (
                    <th
                      key={col.key}
                      className="text-left py-3 px-4 cursor-pointer select-none hover:bg-gray-50 transition-colors"
                      title={col.title || `Ordenar por ${col.label}`}
                      onClick={() => handleSort(col.key)}
                    >
                      <span className="inline-flex items-center">
                        {col.label}
                        <SortIcon column={col.key} />
                      </span>
                    </th>
                  ))}
                </tr>
                {/* ── Fila de filtros por columna ── */}
                <tr className="border-b bg-blue-50/40">
                  <th className="py-1.5 px-4">
                    {hasColFilters && (
                      <button
                        onClick={clearColFilters}
                        title="Limpiar filtros"
                        className="text-red-500 hover:text-red-700 text-xs"
                      >✕</button>
                    )}
                  </th>
                  {/* NUI */}
                  <th className="py-1.5 px-2">
                    <input
                      value={columnFilters.nui}
                      onChange={e => setColFilter('nui', e.target.value)}
                      placeholder="Filtrar…"
                      className="w-full text-xs border border-blue-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                    />
                  </th>
                  {/* Apellido Paterno */}
                  <th className="py-1.5 px-2">
                    <input
                      value={columnFilters.apellidoPaterno}
                      onChange={e => setColFilter('apellidoPaterno', e.target.value)}
                      placeholder="Filtrar…"
                      className="w-full text-xs border border-blue-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                    />
                  </th>
                  {/* Apellido Materno */}
                  <th className="py-1.5 px-2">
                    <input
                      value={columnFilters.apellidoMaterno}
                      onChange={e => setColFilter('apellidoMaterno', e.target.value)}
                      placeholder="Filtrar…"
                      className="w-full text-xs border border-blue-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                    />
                  </th>
                  {/* Nombre */}
                  <th className="py-1.5 px-2">
                    <input
                      value={columnFilters.nombre}
                      onChange={e => setColFilter('nombre', e.target.value)}
                      placeholder="Filtrar…"
                      className="w-full text-xs border border-blue-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                    />
                  </th>
                  {/* Email */}
                  <th className="py-1.5 px-2">
                    <input
                      value={columnFilters.email}
                      onChange={e => setColFilter('email', e.target.value)}
                      placeholder="Filtrar…"
                      className="w-full text-xs border border-blue-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                    />
                  </th>
                  {/* Teléfono */}
                  <th className="py-1.5 px-2">
                    <input
                      value={columnFilters.telefono}
                      onChange={e => setColFilter('telefono', e.target.value)}
                      placeholder="Filtrar…"
                      className="w-full text-xs border border-blue-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                    />
                  </th>
                  {/* Estatus — dropdown */}
                  <th className="py-1.5 px-2">
                    <select
                      value={columnFilters.estatus}
                      onChange={e => setColFilter('estatus', e.target.value)}
                      className="w-full text-xs border border-blue-200 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                    >
                      <option value="">Todos</option>
                      <option value="aspirante">Aspirante</option>
                      <option value="aceptado">Aceptado</option>
                      <option value="activo">Activo</option>
                      <option value="deudor">Deudor</option>
                      <option value="programa_concluido">Prog. Concluido</option>
                      <option value="liberado">Liberado</option>
                      <option value="graduado">Graduado</option>
                      <option value="egresado">Egresado</option>
                      <option value="titulado">Titulado</option>
                      <option value="baja_temporal">Baja Temporal</option>
                      <option value="baja_definitiva">Baja Definitiva</option>
                      <option value="baja_administrativa">Baja Adm.</option>
                      <option value="en_espera">En Espera</option>
                      <option value="non_grata">NON GRATA</option>
                    </select>
                  </th>
                  {/* Programa */}
                  <th className="py-1.5 px-2">
                    <input
                      value={columnFilters.programa}
                      onChange={e => setColFilter('programa', e.target.value)}
                      placeholder="Filtrar…"
                      className="w-full text-xs border border-blue-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                    />
                  </th>
                  {/* Grupo */}
                  <th className="py-1.5 px-2">
                    <input
                      value={columnFilters.grupo}
                      onChange={e => setColFilter('grupo', e.target.value)}
                      placeholder="Filtrar…"
                      className="w-full text-xs border border-blue-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAlumnos.length === 0 && alumnos.length > 0 && (
                  <tr>
                    <td colSpan={10} className="py-10 text-center text-sm text-gray-400">
                      Ningún alumno coincide con los filtros de columna.{' '}
                      <button onClick={clearColFilters} className="text-blue-500 hover:underline">Limpiar filtros</button>
                    </td>
                  </tr>
                )}
                {filteredAlumnos.map((alumno) => (
                  <tr key={alumno.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewDetails(alumno)}
                          title="Ver detalles"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenCodigosPago(alumno)}
                          title="Códigos de pago"
                          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                        >
                          <Hash className="w-4 h-4" />
                        </Button>
                        {alumno.estatus === 'aceptado' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSendWelcomeEmail(alumno)}
                            title="Enviar correo de bienvenida"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditForm(alumno)}
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteAlumno(alumno.id)}
                          title="Eliminar"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono text-sm">
                      {alumno.numeroIdentificacionUnico}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {alumno.apellidoPaterno}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {alumno.apellidoMaterno || ''}
                    </td>
                    <td className="py-3 px-4 text-sm font-medium">
                      {alumno.nombre}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {alumno.email || 'Sin email'}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <div className="flex items-center gap-2">
                        <span>{alumno.telefono || 'Sin teléfono'}</span>
                        {alumno.telefono && alumno.telefono.length >= 10 && (
                          <a
                            href={`https://wa.me/${alumno.telefono.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Enviar WhatsApp"
                            className="text-green-500 hover:text-green-700 transition-colors"
                          >
                            <MessageCircle className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(alumno.estatus)}`}>
                        {getStatusText(alumno.estatus)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {alumno.programId 
                        ? academicPrograms.find(p => p.id === alumno.programId)?.name || 'No encontrado'
                        : 'Sin asignar'}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {alumno.groupId
                        ? academicPeriods.find(p => p.id === alumno.groupId)?.name || 'No encontrado'
                        : 'Sin asignar'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación — oculta cuando hay filtros de columna activos (todos los registros ya están cargados) */}
          {!hasColFilters && totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-500">
                Página {currentPage} de {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de crear alumno */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">Nuevo Alumno</h2>

            {/* Error banner dentro del modal */}
            {error && (
              <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-800">⚠ {error}</p>
                    {Object.keys(fieldErrors).length > 0 && (
                      <ul className="mt-1 list-disc list-inside space-y-0.5">
                        {Object.entries(fieldErrors).map(([field, msg]) => (
                          <li key={field} className="text-xs text-red-700">{msg}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <button
                    onClick={() => { setError(null); setFieldErrors({}); }}
                    className="shrink-0 text-red-400 hover:text-red-600 text-lg leading-none"
                    aria-label="Cerrar"
                  >✕</button>
                </div>
              </div>
            )}
            
            {/* Datos básicos */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Referencia Externa
                  </label>
                  <Input
                    value={newAlumno.numeroReferenciaExterna}
                    onChange={(e) => setNewAlumno({...newAlumno, numeroReferenciaExterna: e.target.value})}
                    placeholder="Opcional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Estatus *
                  </label>
                  <select
                    value={newAlumno.estatus}
                    onChange={(e) => setNewAlumno({...newAlumno, estatus: e.target.value as any})}
                    className={`flex h-10 w-full rounded-md border border-input px-3 py-2 text-sm ${!canManageStatus ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-70' : 'bg-background'}`}
                    required
                    disabled={!canManageStatus}
                  >
                    <optgroup label="── Flujo de ingreso">
                      <option value="prospecto">Prospecto</option>
                      <option value="aspirante">Aspirante</option>
                      <option value="aceptado">Aceptado</option>
                      <option value="activo">Activo</option>
                    </optgroup>
                    <optgroup label="── Situación financiera">
                      <option value="deudor">Deudor</option>
                    </optgroup>
                    <optgroup label="── Flujo de egreso">
                      <option value="programa_concluido">Programa Concluido</option>
                      <option value="liberado">Liberado</option>
                      <option value="graduado">Graduado</option>
                      <option value="egresado">Egresado</option>
                      <option value="titulado">Titulado</option>
                    </optgroup>
                    <optgroup label="── Bajas">
                      <option value="baja_temporal">Baja Temporal</option>
                      <option value="baja_definitiva">Baja Definitiva</option>
                      <option value="baja_administrativa">Baja Administrativa</option>
                      <option value="en_espera">En Espera</option>
                      <option value="non_grata">NON GRATA</option>
                    </optgroup>
                  </select>
                  {!canManageStatus && (
                    <p className="text-xs text-gray-400 mt-1">Sin permiso para cambiar estatus</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Nombre *
                  </label>
                  <Input
                    value={newAlumno.nombre}
                    onChange={(e) => setNewAlumno({...newAlumno, nombre: e.target.value})}
                    required
                    className={fieldErrors.nombre ? "border-red-500" : ""}
                  />
                  {fieldErrors.nombre && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.nombre}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Apellido Paterno *
                  </label>
                  <Input
                    value={newAlumno.apellidoPaterno}
                    onChange={(e) => setNewAlumno({...newAlumno, apellidoPaterno: e.target.value})}
                    required
                    className={fieldErrors.apellidoPaterno ? "border-red-500" : ""}
                  />
                  {fieldErrors.apellidoPaterno && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.apellidoPaterno}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Apellido Materno
                  </label>
                  <Input
                    value={newAlumno.apellidoMaterno}
                    onChange={(e) => setNewAlumno({...newAlumno, apellidoMaterno: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={newAlumno.email}
                    onChange={(e) => setNewAlumno({...newAlumno, email: e.target.value})}
                    className={fieldErrors.email ? "border-red-500" : ""}
                  />
                  {fieldErrors.email && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Teléfono
                  </label>
                  <Input
                    value={newAlumno.telefono}
                    onChange={(e) => setNewAlumno({...newAlumno, telefono: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Contacto de Emergencia
                  </label>
                  <Input
                    value={newAlumno.contactoEmergencia}
                    onChange={(e) => setNewAlumno({...newAlumno, contactoEmergencia: e.target.value})}
                    placeholder="Nombre del contacto"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Teléfono de Emergencia
                  </label>
                  <Input
                    value={newAlumno.telefonoEmergencia}
                    onChange={(e) => setNewAlumno({...newAlumno, telefonoEmergencia: e.target.value})}
                    placeholder="Teléfono del contacto"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-semibold">Inscripciones (Programa y Grupo)</label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setNewAlumno({
                      ...newAlumno,
                      enrollments: [...newAlumno.enrollments, { programId: null, groupId: null, isPrimary: false, status: 'activo' }]
                    })}
                  >
                    + Agregar Inscripción
                  </Button>
                </div>
                {newAlumno.enrollments.map((enrollment, idx) => (
                  <div key={idx} className="border rounded-lg p-3 bg-gray-50 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-500">
                        Inscripción {idx + 1} {enrollment.isPrimary && <span className="text-blue-600 ml-1">(Principal)</span>}
                      </span>
                      <div className="flex gap-2">
                        {!enrollment.isPrimary && (
                          <button
                            type="button"
                            className="text-xs text-blue-600 hover:underline"
                            onClick={() => {
                              const updated = newAlumno.enrollments.map((e, i) => ({ ...e, isPrimary: i === idx }));
                              setNewAlumno({ ...newAlumno, enrollments: updated });
                            }}
                          >
                            Hacer principal
                          </button>
                        )}
                        {newAlumno.enrollments.length > 1 && (
                          <button
                            type="button"
                            className="text-xs text-red-600 hover:underline"
                            onClick={() => {
                              const updated = newAlumno.enrollments.filter((_, i) => i !== idx);
                              if (enrollment.isPrimary && updated.length > 0) updated[0].isPrimary = true;
                              setNewAlumno({ ...newAlumno, enrollments: updated });
                            }}
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">Programa *</label>
                        <SearchableSelect
                          options={academicPrograms.map(p => ({ value: p.id, label: `${p.code} - ${p.name}` }))}
                          value={enrollment.programId}
                          onChange={(val) => {
                            const programId = val ? Number(val) : null;
                            const updated = [...newAlumno.enrollments];
                            updated[idx] = { ...updated[idx], programId, groupId: null };
                            setNewAlumno({ ...newAlumno, enrollments: updated });
                          }}
                          placeholder="Buscar programa..."
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Grupo</label>
                        <SearchableSelect
                          options={academicPeriods
                            .filter((period) => period.programId === enrollment.programId)
                            .map(p => ({ value: p.id, label: `${p.code} - ${p.name}` }))}
                          value={enrollment.groupId}
                          onChange={(val) => {
                            const updated = [...newAlumno.enrollments];
                            updated[idx] = { ...updated[idx], groupId: val ? Number(val) : null };
                            setNewAlumno({ ...newAlumno, enrollments: updated });
                          }}
                          placeholder={enrollment.programId ? 'Buscar grupo...' : 'Primero seleccione programa'}
                          disabled={!enrollment.programId}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button 
                variant="outline" 
                onClick={() => setShowCreateForm(false)}
                disabled={createLoading}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleCreateAlumno}
                disabled={createLoading}
              >
                {createLoading ? 'Creando...' : 'Crear Alumno'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de editar alumno */}
      {showEditForm && editingAlumno && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">
              Editar Alumno - {editingAlumno.numeroIdentificacionUnico}
            </h2>

            {/* Error banner dentro del modal */}
            {error && (
              <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-800">⚠ {error}</p>
                    {Object.keys(fieldErrors).length > 0 && (
                      <ul className="mt-1 list-disc list-inside space-y-0.5">
                        {Object.entries(fieldErrors).map(([field, msg]) => (
                          <li key={field} className="text-xs text-red-700">{msg}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <button
                    onClick={() => { setError(null); setFieldErrors({}); }}
                    className="shrink-0 text-red-400 hover:text-red-600 text-lg leading-none"
                    aria-label="Cerrar"
                  >✕</button>
                </div>
              </div>
            )}
            
            {/* Similar form structure as create form but with editAlumno state */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Referencia Externa
                  </label>
                  <Input
                    value={editAlumno.numeroReferenciaExterna}
                    onChange={(e) => setEditAlumno({...editAlumno, numeroReferenciaExterna: e.target.value})}
                    placeholder="Opcional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Estatus *
                  </label>
                  <select
                    value={editAlumno.estatus}
                    onChange={(e) => setEditAlumno({...editAlumno, estatus: e.target.value as any})}
                    className={`flex h-10 w-full rounded-md border border-input px-3 py-2 text-sm ${!canManageStatus ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-70' : 'bg-background'}`}
                    required
                    disabled={!canManageStatus}
                  >
                    <optgroup label="── Flujo de ingreso">
                      <option value="prospecto">Prospecto</option>
                      <option value="aspirante">Aspirante</option>
                      <option value="aceptado">Aceptado</option>
                      <option value="activo">Activo</option>
                    </optgroup>
                    <optgroup label="── Situación financiera">
                      <option value="deudor">Deudor</option>
                    </optgroup>
                    <optgroup label="── Flujo de egreso">
                      <option value="programa_concluido">Programa Concluido</option>
                      <option value="liberado">Liberado</option>
                      <option value="graduado">Graduado</option>
                      <option value="egresado">Egresado</option>
                      <option value="titulado">Titulado</option>
                    </optgroup>
                    <optgroup label="── Bajas">
                      <option value="baja_temporal">Baja Temporal</option>
                      <option value="baja_definitiva">Baja Definitiva</option>
                      <option value="baja_administrativa">Baja Administrativa</option>
                      <option value="en_espera">En Espera</option>
                      <option value="non_grata">NON GRATA</option>
                    </optgroup>
                  </select>
                  {!canManageStatus && (
                    <p className="text-xs text-gray-400 mt-1">Sin permiso para cambiar estatus</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Nombre *
                  </label>
                  <Input
                    value={editAlumno.nombre}
                    onChange={(e) => setEditAlumno({...editAlumno, nombre: e.target.value})}
                    required
                    className={fieldErrors.nombre ? "border-red-500" : ""}
                  />
                  {fieldErrors.nombre && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.nombre}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Apellido Paterno *
                  </label>
                  <Input
                    value={editAlumno.apellidoPaterno}
                    onChange={(e) => setEditAlumno({...editAlumno, apellidoPaterno: e.target.value})}
                    required
                    className={fieldErrors.apellidoPaterno ? "border-red-500" : ""}
                  />
                  {fieldErrors.apellidoPaterno && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.apellidoPaterno}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Apellido Materno
                  </label>
                  <Input
                    value={editAlumno.apellidoMaterno}
                    onChange={(e) => setEditAlumno({...editAlumno, apellidoMaterno: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={editAlumno.email}
                    onChange={(e) => setEditAlumno({...editAlumno, email: e.target.value})}
                    className={fieldErrors.email ? "border-red-500" : ""}
                  />
                  {fieldErrors.email && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Teléfono
                  </label>
                  <Input
                    value={editAlumno.telefono}
                    onChange={(e) => setEditAlumno({...editAlumno, telefono: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Contacto de Emergencia
                  </label>
                  <Input
                    value={editAlumno.contactoEmergencia}
                    onChange={(e) => setEditAlumno({...editAlumno, contactoEmergencia: e.target.value})}
                    placeholder="Nombre del contacto"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Teléfono de Emergencia
                  </label>
                  <Input
                    value={editAlumno.telefonoEmergencia}
                    onChange={(e) => setEditAlumno({...editAlumno, telefonoEmergencia: e.target.value})}
                    placeholder="Teléfono del contacto"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-semibold">Inscripciones (Programa y Grupo)</label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditAlumno({
                      ...editAlumno,
                      enrollments: [...editAlumno.enrollments, { programId: null, groupId: null, isPrimary: false, status: 'activo' }]
                    })}
                  >
                    + Agregar Inscripción
                  </Button>
                </div>
                {editAlumno.enrollments.map((enrollment, idx) => (
                  <div key={idx} className="border rounded-lg p-3 bg-gray-50 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-500">
                        Inscripción {idx + 1} {enrollment.isPrimary && <span className="text-blue-600 ml-1">(Principal)</span>}
                      </span>
                      <div className="flex gap-2">
                        {!enrollment.isPrimary && (
                          <button
                            type="button"
                            className="text-xs text-blue-600 hover:underline"
                            onClick={() => {
                              const updated = editAlumno.enrollments.map((e, i) => ({ ...e, isPrimary: i === idx }));
                              setEditAlumno({ ...editAlumno, enrollments: updated });
                            }}
                          >
                            Hacer principal
                          </button>
                        )}
                        {editAlumno.enrollments.length > 1 && (
                          <button
                            type="button"
                            className="text-xs text-red-600 hover:underline"
                            onClick={() => {
                              const updated = editAlumno.enrollments.filter((_, i) => i !== idx);
                              if (enrollment.isPrimary && updated.length > 0) updated[0].isPrimary = true;
                              setEditAlumno({ ...editAlumno, enrollments: updated });
                            }}
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">Programa *</label>
                        <SearchableSelect
                          options={academicPrograms.map(p => ({ value: p.id, label: `${p.code} - ${p.name}` }))}
                          value={enrollment.programId}
                          onChange={(val) => {
                            const programId = val ? Number(val) : null;
                            const updated = [...editAlumno.enrollments];
                            updated[idx] = { ...updated[idx], programId, groupId: null };
                            setEditAlumno({ ...editAlumno, enrollments: updated });
                          }}
                          placeholder="Buscar programa..."
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Grupo</label>
                        <SearchableSelect
                          options={academicPeriods
                            .filter((period) => period.programId === enrollment.programId)
                            .map(p => ({ value: p.id, label: `${p.code} - ${p.name}` }))}
                          value={enrollment.groupId}
                          onChange={(val) => {
                            const updated = [...editAlumno.enrollments];
                            updated[idx] = { ...updated[idx], groupId: val ? Number(val) : null };
                            setEditAlumno({ ...editAlumno, enrollments: updated });
                          }}
                          placeholder={enrollment.programId ? 'Buscar grupo...' : 'Primero seleccione programa'}
                          disabled={!enrollment.programId}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowEditForm(false);
                  setEditingAlumno(null);
                }}
                disabled={editLoading}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleEditAlumno}
                disabled={editLoading || paymentPreviewLoading}
              >
                {paymentPreviewLoading ? 'Verificando cobros...' : editLoading ? 'Actualizando...' : 'Actualizar Alumno'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalles */}
      {showDetails && detailAlumno && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">
              Detalles del Alumno - {detailAlumno.numeroIdentificacionUnico}
            </h2>

            {/* Tabs */}
            <div className="flex border-b mb-4">
              <button
                onClick={() => setDetailTab('info')}
                className={`px-4 py-2 font-medium text-sm ${detailTab === 'info' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Información General
              </button>
              <button
                onClick={() => setDetailTab('academic')}
                className={`px-4 py-2 font-medium text-sm ${detailTab === 'academic' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Materias y Asistencias
              </button>
              <button
                onClick={() => setDetailTab('status')}
                className={`px-4 py-2 font-medium text-sm ${detailTab === 'status' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Historial de Estatus
              </button>
            </div>
            
            {/* Tab: Información General */}
            {detailTab === 'info' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Folio</label>
                    <p className="font-mono">{detailAlumno.numeroIdentificacionUnico}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Referencia Externa</label>
                    <p>{detailAlumno.numeroReferenciaExterna || 'No especificada'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Nombre Completo</label>
                    <p>{detailAlumno.nombre} {detailAlumno.apellidoPaterno} {detailAlumno.apellidoMaterno}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Estatus</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(detailAlumno.estatus)}`}>
                      {getStatusText(detailAlumno.estatus)}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Email</label>
                    <p>{detailAlumno.email || 'No especificado'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Teléfono</label>
                    <p>{detailAlumno.telefono || 'No especificado'}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-500">Inscripciones (Programa / Grupo)</label>
                  {detailAlumno.enrollments && detailAlumno.enrollments.length > 0 ? (
                    <div className="space-y-2">
                      {detailAlumno.enrollments.map((enr, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm bg-gray-50 rounded-lg p-2">
                          <span className="font-medium">{enr.programName || academicPrograms.find(p => p.id === enr.programId)?.name || 'Programa'}</span>
                          <span className="text-gray-400">/</span>
                          <span>{enr.groupName || academicPeriods.find(p => p.id === (enr.groupId ?? 0))?.name || 'Sin grupo'}</span>
                          {enr.isPrimary && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Principal</span>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>
                      {detailAlumno.programId 
                        ? academicPrograms.find(p => p.id === detailAlumno.programId)?.name || 'No encontrado'
                        : 'Sin asignar'}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Fecha de Creación</label>
                    <p>{new Date(detailAlumno.fechaCreacion).toLocaleDateString('es-MX')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Última Actualización</label>
                    <p>{new Date(detailAlumno.fechaActualizacion).toLocaleDateString('es-MX')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Tab: Materias y Asistencias */}
            {detailTab === 'academic' && (
              <div className="space-y-6">
                {loadingAcademicRecord ? (
                  <div className="text-center py-8 text-gray-500">Cargando historial académico...</div>
                ) : academicRecord ? (
                  <>
                    {/* Información del programa y período */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Programa</label>
                          <p className="font-medium">{academicRecord.program?.name || 'Sin asignar'}</p>
                          {academicRecord.program?.code && (
                            <p className="text-sm text-gray-500">{academicRecord.program.code}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Grupo</label>
                          <p className="font-medium">{academicRecord.period?.name || 'Sin asignar'}</p>
                          {academicRecord.period?.code && (
                            <p className="text-sm text-gray-500">{academicRecord.period.code}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Resumen de Asistencias por Materia */}
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Resumen de Asistencias</h3>
                      {academicRecord.attendanceSummary.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-4 py-2 text-left">Materia</th>
                                <th className="px-4 py-2 text-center">Total</th>
                                <th className="px-4 py-2 text-center text-green-600">Presentes</th>
                                <th className="px-4 py-2 text-center text-red-600">Ausentes</th>
                                <th className="px-4 py-2 text-center text-blue-600">Justificados</th>
                                <th className="px-4 py-2 text-center text-yellow-600">Tardanzas</th>
                                <th className="px-4 py-2 text-center">% Asistencia</th>
                              </tr>
                            </thead>
                            <tbody>
                              {academicRecord.attendanceSummary.map((summary) => {
                                const attendanceRate = summary.totalClasses > 0 
                                  ? ((Number(summary.present) + Number(summary.justified)) / Number(summary.totalClasses) * 100).toFixed(1)
                                  : '0';
                                return (
                                  <tr key={summary.courseId} className="border-b hover:bg-gray-50">
                                    <td className="px-4 py-2">
                                      <div className="font-medium">{summary.courseName}</div>
                                      <div className="text-xs text-gray-500">{summary.courseCode}</div>
                                    </td>
                                    <td className="px-4 py-2 text-center">{summary.totalClasses}</td>
                                    <td className="px-4 py-2 text-center text-green-600 font-medium">{summary.present}</td>
                                    <td className="px-4 py-2 text-center text-red-600 font-medium">{summary.absent}</td>
                                    <td className="px-4 py-2 text-center text-blue-600 font-medium">{summary.justified}</td>
                                    <td className="px-4 py-2 text-center text-yellow-600 font-medium">{summary.late}</td>
                                    <td className="px-4 py-2 text-center">
                                      <span className={`font-medium ${Number(attendanceRate) >= 80 ? 'text-green-600' : Number(attendanceRate) >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                                        {attendanceRate}%
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-4">No hay registros de asistencia</p>
                      )}
                    </div>

                    {/* Lista de Materias/Horarios */}
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Materias Asignadas</h3>
                      {academicRecord.courses.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {/* Agrupar por materia única */}
                          {Array.from(new Set(academicRecord.courses.map(c => c.courseId))).map(courseId => {
                            const course = academicRecord.courses.find(c => c.courseId === courseId);
                            if (!course) return null;
                            const schedules = academicRecord.courses.filter(c => c.courseId === courseId);
                            return (
                              <div key={courseId} className="border rounded-lg p-3 hover:shadow-sm">
                                <div className="font-medium">{course.courseName}</div>
                                <div className="text-sm text-gray-500 mb-2">{course.courseCode}</div>
                                <div className="text-xs space-y-1">
                                  {schedules.map((s, idx) => (
                                    <div key={idx} className="flex justify-between">
                                      <span className="capitalize">{s.day}</span>
                                      <span>{s.startTime} - {s.endTime}</span>
                                    </div>
                                  ))}
                                  {course.professorName && (
                                    <div className="text-gray-500 mt-1">Prof: {course.professorName}</div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-4">No hay materias asignadas a este grupo</p>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No se pudo cargar el historial académico. Verifica que el alumno tenga un programa y grupo asignados.
                  </div>
                )}
              </div>
            )}

            {/* Tab: Historial de Estatus */}
            {detailTab === 'status' && (
              <div className="space-y-6">
                {loadingStatus ? (
                  <div className="text-center py-8 text-gray-500">Cargando historial...</div>
                ) : (
                  <>
                    {/* Historial de cambios */}
                    <div>
                      <h3 className="text-base font-semibold mb-3">Cambios de Estatus</h3>
                      {statusHistory.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">Sin cambios registrados</p>
                      ) : (
                        <div className="space-y-2">
                          {statusHistory.map((entry) => (
                            <div key={entry.id} className="flex items-start gap-3 border rounded-lg px-3 py-2.5 text-sm">
                              <div className="mt-0.5 flex-shrink-0">
                                {entry.autoTriggered ? (
                                  <span className="inline-block bg-purple-100 text-purple-700 text-[10px] font-bold px-1.5 py-0.5 rounded">AUTO</span>
                                ) : (
                                  <span className="inline-block bg-gray-100 text-gray-600 text-[10px] font-bold px-1.5 py-0.5 rounded">MANUAL</span>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-gray-500">{entry.oldStatus ?? '—'}</span>
                                  <span className="text-gray-400">→</span>
                                  <span className="font-medium text-gray-900">{entry.newStatus}</span>
                                </div>
                                {entry.reason && (
                                  <p className="text-xs text-gray-500 mt-0.5 truncate">{entry.reason}</p>
                                )}
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {new Date(entry.createdAt).toLocaleString('es-MX')}
                                  {entry.changedByName ? ` · ${entry.changedByName}` : ''}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Log de evaluaciones del engine */}
                    <div>
                      <h3 className="text-base font-semibold mb-1">Log de Evaluaciones del Engine</h3>
                      <p className="text-xs text-gray-500 mb-3">Registro completo de reglas evaluadas (aplicadas y omitidas)</p>
                      {statusEvaluations.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">Sin evaluaciones registradas</p>
                      ) : (
                        <div className="space-y-2">
                          {statusEvaluations.map((ev) => (
                            <div key={ev.id} className={`border rounded-lg px-3 py-2.5 text-sm ${ev.applied ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                              <div className="flex items-center gap-2 flex-wrap">
                                {ev.applied ? (
                                  <span className="inline-block bg-green-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">APLICADO</span>
                                ) : (
                                  <span className="inline-block bg-gray-300 text-gray-700 text-[10px] font-bold px-1.5 py-0.5 rounded">OMITIDO</span>
                                )}
                                <span className="font-mono text-xs text-blue-700">{ev.ruleId}</span>
                                <span className="text-xs text-gray-500">trigger: {ev.trigger}</span>
                              </div>
                              <div className="flex gap-4 mt-1 text-xs text-gray-600">
                                <span>Estatus actual: <strong>{ev.currentStatus ?? '—'}</strong></span>
                                {ev.proposedStatus && (
                                  <span>Propuesto: <strong>{ev.proposedStatus}</strong></span>
                                )}
                                {ev.skipReason && (
                                  <span className="text-amber-700">Motivo omisión: {ev.skipReason}</span>
                                )}
                              </div>
                              {ev.conditionSnapshot && (
                                <details className="mt-1">
                                  <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">Ver snapshot</summary>
                                  <pre className="text-[10px] text-gray-500 mt-1 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(ev.conditionSnapshot, null, 2)}</pre>
                                </details>
                              )}
                              <p className="text-xs text-gray-400 mt-1">{new Date(ev.evaluatedAt).toLocaleString('es-MX')}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDetails(false);
                  setDetailAlumno(null);
                }}
              >
                Cerrar
              </Button>
              <Button 
                onClick={() => {
                  setShowDetails(false);
                  setDetailAlumno(null);
                  openEditForm(detailAlumno);
                }}
              >
                Editar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para solicitar email */}
      {showEmailModal && selectedAlumnoForEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">
              Enviar Correo de Bienvenida
            </h2>
            
            <div className="space-y-4">
              <p className="text-gray-600">
                El alumno <strong>{selectedAlumnoForEmail.nombre} {selectedAlumnoForEmail.apellidoPaterno}</strong> no tiene un correo electrónico registrado.
              </p>
              
              <div>
                <label className="block text-sm font-medium mb-2">
                  Ingresa el correo electrónico del alumno:
                </label>
                <Input
                  type="email"
                  value={tempEmail}
                  onChange={(e) => setTempEmail(e.target.value)}
                  placeholder="alumno@ejemplo.com"
                  className="w-full"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowEmailModal(false);
                  setSelectedAlumnoForEmail(null);
                  setTempEmail('');
                }}
                disabled={sendingEmail}
              >
                Cancelar
              </Button>
              <Button 
                onClick={() => {
                  if (!tempEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tempEmail)) {
                    alert('Por favor ingresa un correo electrónico válido');
                    return;
                  }
                  handleSendEmail(selectedAlumnoForEmail, tempEmail);
                }}
                disabled={sendingEmail}
              >
                {sendingEmail ? 'Enviando...' : 'Enviar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Códigos de Pago */}
      {showCodigosPago && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b">
              <div className="flex items-center gap-2">
                <Hash className="w-5 h-5 text-emerald-600" />
                <div>
                  <h2 className="font-semibold text-gray-900 text-base">Códigos de Pago</h2>
                  {codigosPagoData && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {codigosPagoData.alumno.nui} — {codigosPagoData.alumno.nombre}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => { setShowCodigosPago(false); setCodigosPagoData(null); }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <span className="text-xl leading-none">&times;</span>
              </button>
            </div>

            {/* Body */}
            <div className="p-5">
              {loadingCodigos ? (
                <div className="flex items-center justify-center py-10">
                  <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mr-3" />
                  <span className="text-sm text-gray-500">Consultando facturas activas...</span>
                </div>
              ) : !codigosPagoData ? (
                <div className="text-center py-8">
                  <p className="text-sm text-red-500">No se pudieron cargar los códigos. Intenta de nuevo.</p>
                </div>
              ) : codigosPagoData.codigos.length === 0 ? (
                <div className="text-center py-8">
                  <Hash className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 font-medium">Sin códigos de pago disponibles</p>
                  <p className="text-xs text-gray-400 mt-1">No hay facturas activas ni producto de colegiatura asignado al grupo</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-gray-400 mb-3">
                    Código = 4 dígitos NUI + 3 dígitos concepto. Comparte con el alumno para realizar su pago.
                  </p>
                  {codigosPagoData.codigos.map((item) => (
                    <div
                      key={item.sku}
                      className="flex items-center justify-between gap-3 bg-gray-50 rounded-lg px-4 py-3 border border-gray-100"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-lg font-bold tracking-widest text-gray-900">
                            {codigosPagoData.alumno.nuiDigits}
                            <span className="text-emerald-600">{item.sku}</span>
                          </span>
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                            item.fuente === 'factura'
                              ? 'bg-blue-100 text-blue-600'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {item.fuente === 'factura' ? 'Factura' : 'Grupo'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-0.5 truncate font-medium">{item.conceptoNombre}</p>
                        {item.referencia && (
                          <p className="text-xs text-gray-400">{item.referencia}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleCopyCode(item.codigoPago)}
                        title="Copiar código"
                        className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                          copiedCode === item.codigoPago
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-white border border-gray-200 text-gray-600 hover:border-emerald-400 hover:text-emerald-600'
                        }`}
                      >
                        {copiedCode === item.codigoPago ? (
                          <><CheckCheck className="w-3.5 h-3.5" /> Copiado</>
                        ) : (
                          <><Copy className="w-3.5 h-3.5" /> Copiar</>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setShowCodigosPago(false); setCodigosPagoData(null); }}
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Diálogo de validación de pago al agregar inscripción ─────────── */}
      {showPaymentConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <span className="text-amber-600 text-lg">⚠</span>
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Validación de cobros — Nueva inscripción</h2>
                <p className="text-xs text-gray-500 mt-0.5">Se detectaron obligaciones financieras para el(los) grupo(s) recién agregado(s)</p>
              </div>
            </div>

            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              {paymentPreviews.map(preview => (
                <div key={preview.groupId} className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b">
                    <p className="text-sm font-semibold text-gray-800">📚 {preview.groupName}</p>
                  </div>
                  <div className="px-4 py-3 space-y-3">
                    {preview.inscriptionFee && (
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-blue-600 text-xs font-bold">$</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">Cargo de inscripción</p>
                          <p className="text-xs text-gray-500">{preview.inscriptionFee.productName}</p>
                          <p className="text-sm text-blue-700 font-semibold">
                            {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(preview.inscriptionFee.price))}
                            {preview.inscriptionFee.taxRate !== '0' && (
                              <span className="text-xs font-normal text-gray-500 ml-1">+ {preview.inscriptionFee.taxRate}% IVA</span>
                            )}
                          </p>
                        </div>
                      </div>
                    )}
                    {preview.activeTuitionPlans.length > 0 && (
                      <div className="space-y-2">
                        {preview.activeTuitionPlans.map(plan => (
                          <div key={plan.id} className="flex items-start gap-3">
                            <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-green-600 text-xs font-bold">#</span>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-800">Plan de colegiatura</p>
                              <p className="text-xs text-gray-500">{plan.name}</p>
                              <div className="flex flex-wrap gap-2 mt-1">
                                <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-200">
                                  {plan.installmentCount} cuotas
                                </span>
                                <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-gray-50 text-gray-600 border border-gray-200">
                                  Total: {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(plan.totalAmount))}
                                </span>
                                <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-600 border border-blue-200">
                                  1ª venc.: {(() => { const [y,m,d] = plan.firstDueDate.split('-').map(Number); return new Date(y,m-1,d).toLocaleDateString('es-MX',{day:'2-digit',month:'short',year:'numeric'}); })()}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                ¿Deseas generar estos cobros automáticamente al guardar la inscripción?
              </div>
            </div>

            <div className="px-6 py-4 border-t flex flex-col sm:flex-row gap-2 sm:justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPaymentConfirmDialog(false)}
                disabled={editLoading}
              >
                Cancelar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { if (pendingSavePayload) performSaveAlumno(pendingSavePayload, false); }}
                disabled={editLoading}
              >
                Solo guardar sin cobros
              </Button>
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => { if (pendingSavePayload) performSaveAlumno(pendingSavePayload, true); }}
                disabled={editLoading}
              >
                {editLoading ? 'Guardando...' : 'Confirmar y generar cobros'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Diálogo de aviso de posible alumno duplicado */}
      {showDuplicateWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <span className="text-amber-600 text-xl">⚠</span>
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Posible duplicado detectado</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Ya existe{duplicateCandidates.length > 1 ? 'n' : ''} {duplicateCandidates.length} alumno{duplicateCandidates.length > 1 ? 's' : ''} con datos similares en el sistema
                </p>
              </div>
            </div>

            <div className="p-6 space-y-3 max-h-[50vh] overflow-y-auto">
              {duplicateCandidates.map(c => (
                <div key={c.id} className="border border-amber-200 bg-amber-50 rounded-lg px-4 py-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-gray-900">
                      {c.nombre} {c.apellidoPaterno} {c.apellidoMaterno}
                    </span>
                    <span className="text-xs text-gray-500 bg-white border border-gray-200 rounded px-1.5 py-0.5 shrink-0">
                      {c.numeroIdentificacionUnico}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-600">
                    {c.email && <span>✉ {c.email}</span>}
                    {c.telefono && <span>📞 {c.telefono}</span>}
                    <span className="capitalize">● {c.estatus}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-lg">
              <p className="text-xs text-gray-500 mb-3">
                ¿El nuevo registro es una persona diferente o deseas continuar de todas formas?
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowDuplicateWarning(false); setPendingCreatePayload(null); }}
                  disabled={createLoading}
                >
                  Cancelar — revisar el formulario
                </Button>
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() => { if (pendingCreatePayload) performCreateAlumno(pendingCreatePayload); }}
                  disabled={createLoading}
                >
                  {createLoading ? 'Creando...' : 'Crear de todas formas'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}